import { Box, Divider, Text } from '@chakra-ui/react'
import { Layout } from 'components/atoms'
import { ModalBuild } from 'components/molecules'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { TableBuild } from 'components/organisms'
import { TableBuildProps } from 'components/organisms/TableBuild/interfaces'
import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import ContractService from 'services/contracts'
import formatter from 'utils/formatter'
import { camelCaseToWords } from 'utils/regex'
import { statusOperation } from 'utils/reusableFunction'
import { Operation, TableHistoricDetail } from './interfaces'

export const HistoricDetail = () => {
    const [loading, setLoading] = useState(false)
    const refModal = useRef<ModalBuildHandle>(null)
    const { id = '' } = useParams()
    const [viewOperation, setViewOperation] = useState<Operation | undefined>()
    const [operations, setOperations] = useState<TableHistoricDetail>()
    const [table, setTable] = useState<
        TableBuildProps<
            | 'operacao'
            | 'dataOperacao'
            | 'usuario'
            | 'nomeUsuario'
            | 'email'
            | 'alocacao'
        >
    >({
        header: [
            {
                name: 'operacao',
                text: 'Tipo Serviço',
                position: 0
            },
            {
                name: 'dataOperacao',
                text: 'Data/Hora Operação',
                position: 1
            },
            {
                name: 'alocacao',
                text: 'Unidade de Alocação',
                position: 2
            },
            {
                name: 'usuario',
                text: 'Usuário',
                position: 3
            },
            {
                name: 'email',
                text: 'Email',
                position: 4
            },
            {
                name: 'nomeUsuario',
                text: 'Nome Usuário',
                position: 5
            }
        ],
        action: [
            {
                type: 'PREVIEW'
            }
        ]
    })

    const [searchParams] = useSearchParams()
    const statusRecused = searchParams.get('status') === 'RECUSADO'
    const buildTable = (historic: TableHistoricDetail) => {
        return historic.operacoes.map(operation => {
            return {
                ...operation,
                operacao: statusOperation(operation.operacao),
                dataOperacao: formatter.dataWithHour(operation.dataOperacao)
            }
        })
    }

    useEffect(() => {
        if (!statusRecused) {
            fetchHistoricByContract()
        }
    }, [])

    async function fetchHistoricByContract() {
        setLoading(true)
        try {
            const response = await ContractService.getHistoricByContractID(id)
            const historicData = response.data
            setOperations(historicData)
            setTable((old: any) => ({
                ...old,
                body: buildTable(historicData as TableHistoricDetail)
            }))
        } finally {
            setLoading(false)
        }
    }

    const handleAction = (action: string, indexRow: number | string) => {
        const operation = operations?.operacoes.find(
            (_, index: number) => index === indexRow
        )
        setViewOperation(operation)
        handleOpenModal()
    }

    const handleOpenModal = (): void => {
        refModal.current?.onOpen()
    }

    return (
        <>
            <ModalBuild
                ref={refModal}
                closeOnOverlayClick={false}
                title=" "
                size="4xl"
            >
                <Box px={8} py={4} overflowY="scroll" maxH="550px">
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                    >
                        <Box marginTop={2}>
                            <Text
                                fontSize="24px"
                                whiteSpace="pre"
                                color="black"
                                fontWeight="700"
                            >
                                {statusOperation(viewOperation?.operacao)}
                            </Text>
                        </Box>
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-end"
                        >
                            <Text
                                marginTop="12px"
                                fontSize="16px"
                                whiteSpace="pre"
                                color="gray"
                                fontWeight="400"
                            >
                                Usuário:{' '}
                                {viewOperation?.usuario ??
                                    'Usuário não encontrado'}
                            </Text>
                            <Text
                                fontSize="16px"
                                marginTop="2px"
                                whiteSpace="pre"
                                color="gray"
                            >
                                Data Operação:{' '}
                                {viewOperation?.dataOperacao
                                    ? formatter.dataWithHour(
                                          viewOperation?.dataOperacao
                                      )
                                    : ''}
                            </Text>
                        </Box>
                    </Box>
                    <Box py={8}>
                        <Layout
                            type="row"
                            gutter={{ y: 1.5 }}
                            data-testid="form-fields"
                        >
                            {viewOperation &&
                                viewOperation.alteracoes.map((changes, key) => (
                                    <div key={key}>
                                        <Layout
                                            layout={{ base: 12, md: 12 }}
                                            type="col"
                                        >
                                            <Box>
                                                <Text
                                                    fontSize="18px"
                                                    fontWeight="600"
                                                    marginBottom="8px"
                                                >
                                                    {camelCaseToWords(
                                                        changes.campo
                                                    )}
                                                </Text>
                                                <Box display="flex">
                                                    <Text
                                                        fontSize="16px"
                                                        fontWeight="400"
                                                        marginRight="24px"
                                                        display="flex"
                                                    >
                                                        <Text
                                                            fontWeight="600"
                                                            marginRight="2px"
                                                        >
                                                            DE:{' '}
                                                        </Text>
                                                        {changes.valorAntigo}
                                                    </Text>
                                                    <Text
                                                        fontSize="16px"
                                                        fontWeight="400"
                                                        display="flex"
                                                    >
                                                        <Text
                                                            fontWeight="600"
                                                            marginRight="2px"
                                                        >
                                                            PARA:{' '}
                                                        </Text>
                                                        {changes.valorAtual}
                                                    </Text>
                                                </Box>
                                            </Box>
                                        </Layout>
                                        <Divider
                                            border="1px"
                                            borderColor="gray"
                                            marginTop="24px"
                                        />
                                    </div>
                                ))}
                        </Layout>
                    </Box>
                </Box>
            </ModalBuild>
            <TableBuild
                header={table.header}
                body={table.body}
                action={table.action}
                loading={{
                    status: loading,
                    loader: [
                        {
                            height: '20px'
                        },
                        {
                            height: '20px'
                        },
                        {
                            height: '20px'
                        },
                        {
                            height: '20px'
                        }
                    ]
                }}
                onAction={handleAction}
            ></TableBuild>
        </>
    )
}
